import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ModalService } from '@purespectrum1/ui/modal';

import {
  Qualification,
  QuotaFlags,
} from '../../shared/interfaces/qualification.interface';
import { SelectedQuota } from '../../shared/interfaces/quota.interface';
import { QuotaModalComponent } from '../quota-modal/quota-modal.component';
import { Flexible } from '../../shared/interfaces/create-surveys.interface';
import { QuotaModalService } from '../quota-modal/quota-modal.service';
import { ConfirmationModalComponent } from '../../shared/ui/confirmation-modal/confirmation-modal.component';
import { Constants } from '../../shared/constants/create-survey.constants';
import { AidaPayloadService } from '../../shared/services/aida/aida-payload/aida-payload.service';
import { AidaPayload } from '../../shared/interfaces/aida.interface';
import { notifyMessage } from '../../shared/constants/notify-message';
import { Subscription } from 'rxjs';
import { AidaSurveyQuotasEditInspector } from '../../shared/domain/aida-survey-quotas-edit-inspector';
import { QuotaFlagsComputer } from '../domain/quota-flags-computer';

@Component({
  selector: 'ps-ui-range-input-button',
  templateUrl: './range-input-button.component.html',
  styleUrls: ['./range-input-button.component.css'],
})
export class RangeInputButtonComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('toggleButton')
  toggleButton!: ElementRef;
  @ViewChild('list')
  list!: ElementRef;

  public quotaFlags: QuotaFlags = {
    add: true,
    edit: false,
    census: false,
    delete: false,
  };
  public rangeLabels: { MIN: string; MAX: string } = Constants.RANGE_LABELS;
  public isOpen: boolean = false;
  public isSelectedForAida: boolean = false;
  public disableCensusBtn: boolean = false;
  private _aida: AidaPayload = { enabled: false, selected_quota_ids: [] };
  private _subscription: Subscription = new Subscription();
  private _qualificationRange = {
    min: 0,
    max: 0,
  };
  @Input() complete!: number;
  @Input() clickBalance!: number;
  @Input() quotas?: SelectedQuota[];
  @Input() flexible?: Flexible;
  @Input() qualification!: Qualification;
  @Input() isQlcLiveSurvey: boolean = false;
  @Output() readonly valueChange = new EventEmitter();
  @Output() readonly quotaChange = new EventEmitter();
  @Input() changeCompletes: EventEmitter<number> = new EventEmitter<number>();
  @Input() hasNestedGroup: boolean = false;
  @Input() isLiveSurveyEdit: boolean = false;
  @Input() disabled: boolean = false;
  @Input() isMcSurvey: boolean = false;

  constructor(
    private _aidaPayloadService: AidaPayloadService,
    private _renderer: Renderer2,
    private _modal: ModalService,
    private _quotaModalService: QuotaModalService
  ) {
    this._renderer.listen('window', 'click', (event: Event) => {
      /**
       * Only run when toggleButton is not clicked
       * If we don't check this, all clicks (even on the toggle button) gets into this
       * section which in the result we might never see the menu open!
       * And the menu itself is checked here, and it's where we check just outside of
       * the menu and button the condition abbove must close the menu
       */
      if (
        event.target !== this.toggleButton.nativeElement &&
        !this.list.nativeElement.contains(event.target)
      ) {
        this.isOpen = false;
      }
    });
  }

  ngOnInit(): void {
    // set default flexibility in case of undefined.
    if (!this.flexible) {
      this.flexible = {
        enable: true,
        value: 0,
      };
    }
    if (this.quotas?.length) {
      // add empty range row on top in live edit page
      this.quotas?.unshift({
        maximum: 0,
        minimum: 0,
        max: 0,
        min: 0,
        number: 0,
        percentage: 0,
      });
    }

    this._setQuotaFlags1();

    this.changeCompletes.subscribe((newCompletes) => {
      if (this.quotas) {
        this.quotas = this._quotaModalService.reDistributeQuotaQuantites(
          this.quotas,
          newCompletes,
          this.flexible
        );
        this.quotaChange.emit({
          msg: Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          qualification: this.qualification,
          quotas: this.quotas,
          flexible: this.flexible,
        });
      }
    });
  }

  ngOnChanges(): void {
    this._setQuotaFlags1();
    this._handleAidaPayload();
  }

  private _handleAidaPayload() {
    this._subscription.add(
      this._aidaPayloadService.getAidaLiveEditPayload().subscribe((payload) => {
        this._aida = payload;
        this._qualificationRange = {
          min: this.qualification.data[0].minValue,
          max: this.qualification.data[0].maxValue,
        };
        this.isSelectedForAida = new AidaSurveyQuotasEditInspector(
          this._aida,
          this.quotas,
          this.qualification.hasNestedQuotas
        ).notEditable();
      })
    );
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  onChange() {
    if (this.isSelectedForAida) {
      const qualificationDetails = this.qualification.data[0];
      if (
        qualificationDetails.maxValue !== this._qualificationRange.max ||
        qualificationDetails.minValue !== this._qualificationRange.min
      ) {
        qualificationDetails.maxValue = this._qualificationRange.max;
        qualificationDetails.minValue = this._qualificationRange.min;
        this._aidaPayloadService.sendAidaError(
          notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
        );
      }
      return;
    }

    this._manageRangeMinMaxvalue();
    if (
      this.quotas?.length ||
      (this.qualification.data[0].minValue &&
        this.qualification.data[0].maxValue)
    ) {
      if (this.qualification.hasNestedQuotas) {
        const modalRef = this._modal.open(ConfirmationModalComponent, {
          data: {
            text: Constants.CONFIRM_MODAL_TEXT.MODIFY_QUALIFICATION,
            confirmButtonLabel: 'Yes',
          },
        });
        modalRef.onClose$.subscribe((msg) => {
          if (msg === 'ok') {
            // delete quotas on any change
            this.checkCensusForMcSurveys(true);
          }
        });
      } else if (this.quotas?.length) {
        // delete quotas on any change
        this.checkCensusForMcSurveys(true);
      }
    }
    this.valueChange.emit(this.qualification);
  }

  private _manageRangeMinMaxvalue() {
    if (!this.qualification.data[0].minValue) {
      this.qualification.data[0].minValue =
        this.qualification.data[0].minPlaceholder;
    }
    if (!this.qualification.data[0].maxValue) {
      this.qualification.data[0].maxValue =
        this.qualification.data[0].maxPlaceholder;
    }
  }

  private _setQuotaFlags1() {
    const computer = new QuotaFlagsComputer(
      this.quotas || [],
      this.qualification
    );
    this.quotaFlags.add = computer.add();
    this.quotaFlags.edit = computer.edit();
    this.quotaFlags.delete = computer.delete();
    this.quotaFlags.census = computer.census();
    // Deciding whether census is applied or not
    this.disableCensusBtn = !!(this.quotaFlags.census && this.quotas?.length);
  }

  openQuotaModal() {
    if (this._quotaModalService.inValidComplete(this.complete)) {
      return;
    }

    const modalRef1 = this._modal.open(QuotaModalComponent, {
      data: {
        quotas: this.quotas,
        complete: this.complete,
        flexible: this.flexible,
        clickBalance: this.clickBalance,
        qualification: this.qualification,
        hasNestedGroup: this.hasNestedGroup,
        isLiveSurveyEdit: this.isLiveSurveyEdit,
        isMcSurvey: this.isMcSurvey,
        isSelectedForAida: this.isSelectedForAida,
      },
      width: '80%',
    });

    modalRef1.onClose$.subscribe((response) => {
      if (
        [
          Constants.QUOTA_MODAL_ACTION.APPLY_QUOTA,
          Constants.QUOTA_MODAL_ACTION.APPLY_NEST_QUOTA,
        ].includes(response?.msg)
      ) {
        this.quotas = response.quotas;
        this.flexible = response.flexible;
        this.quotaFlags = {
          add: false,
          edit: true,
          census: true,
          delete: true,
        };
        this.quotaChange.emit(response);
        this.disableCensusBtn = true;
      }
    });
  }

  checkCensusForMcSurveys(isDelete: boolean = false) {
    const applyToAllCountries = () => {
      if (isDelete) {
        this._actionAfterDeleteQuotas(true);
      } else {
        this.applyRangeCensus(true);
      }
    };

    const applyToThisCountryOnly = () => {
      if (isDelete) {
        this._actionAfterDeleteQuotas();
      } else {
        this.applyRangeCensus();
      }
    };

    if (this.isMcSurvey) {
      const message = isDelete
        ? Constants.CONFIRM_MODAL_TEXT.DELETE_CENSUS_TO_ALL_MC
        : Constants.CONFIRM_MODAL_TEXT.CENSUS_APPLY_TO_ALL_MC;
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: message,
          cancelButtonLabel: 'Apply only in this locale',
          confirmButtonLabel: 'Apply to all locales',
          showCloseButton: true,
        },
        width: '50%',
      });

      modalRef.onClose$.subscribe((msg) => {
        if (msg === 'ok') {
          applyToAllCountries();
        } else if (!!msg) {
          applyToThisCountryOnly();
        }
      });
    } else {
      applyToThisCountryOnly();
    }
  }

  checkAndApplyRangeCensus() {
    if (this.isMcSurvey) {
      return this.checkCensusForMcSurveys(false);
    }

    if (this.quotaFlags.edit && !this.disableCensusBtn) {
      const modalRef = this._modal.open(ConfirmationModalComponent, {
        data: {
          text: Constants.CONFIRM_MODAL_TEXT.ADD_CENSUS,
        },
      });
      modalRef.onClose$.subscribe((mssg) => {
        if (mssg == 'ok') {
          this.applyRangeCensus();
        }
      });
    } else {
      this.applyRangeCensus();
    }
  }

  public applyRangeCensus(
    isMc: boolean = false,
    qualification: Qualification = this.qualification,
    quotas: SelectedQuota[] = this.quotas || [],
    completes = this.complete,
    flexible = this.flexible,
    isOutSideCall: boolean = false
  ) {
    if (this._quotaModalService.inValidComplete(completes)) {
      return;
    }

    const distributedQuotas = this._distributeQuotas(qualification);
    const mappedQuotas = this._adjustQuotaQuantites(distributedQuotas);

    if (mappedQuotas.length) {
      quotas = mappedQuotas;
      const [min, max] = quotas.reduce(
        (acc, cur, index) => {
          if (index) {
            return [
              Math.min(Number(cur.min || 0), acc[0]),
              Math.max(Number(cur.max || 0), acc[1]),
            ];
          }
          return acc;
        },
        [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]
      );
      qualification.data[0].minValue = min;
      qualification.data[0].maxValue = max;

      if (isOutSideCall) {
        return quotas;
      }

      this.quotas = quotas;
      this.qualification = qualification;

      this.disableCensusBtn = true;
      this.quotaFlags = {
        add: false,
        edit: true,
        census: true,
        delete: true,
      };

      return this.quotaChange.emit({
        msg: 'applyCensus',
        qualification: this.qualification,
        quotas: this.quotas,
        flexible,
        isMc,
      });
    }
  }

  private _distributeQuotas(qualification: Qualification = this.qualification) {
    if (qualification.census?.data?.length) {
      const censusData = [...qualification.census.data];
      const emptyRow = {
        min: null,
        max: null,
        rep_value: 0,
      };
      censusData.unshift(emptyRow);

      return censusData.map((census) => {
        let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
          census.rep_value,
          this.flexible
        );
        return {
          min: census.min,
          max: census.max,
          number: Math.round((census.rep_value * Number(this.complete)) / 100),
          percentage: Number(census.rep_value),
          minimum: Number(minimum),
          maximum: Number(maximum),
        };
      });
    }
    return [];
  }

  private _adjustQuotaQuantites(distributedQuotas: SelectedQuota[]) {
    if (distributedQuotas.length) {
      const isCensusApplied = true;
      return distributedQuotas.map((quota, index) => {
        const number = this._quotaModalService.getNumber(
          this.complete,
          distributedQuotas,
          quota.percentage,
          index,
          isCensusApplied
        );
        let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
          number,
          this.flexible
        );
        let obj = {
          min: quota.min,
          max: quota.max,
          number: number,
          percentage: quota.percentage,
          minimum: minimum,
          maximum: maximum,
        };
        distributedQuotas[index] = obj;
        return obj;
      });
    }

    return [];
  }

  deleteQuotas() {
    if (this.isSelectedForAida) {
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
      return;
    }

    if (this.isMcSurvey) {
      return this.checkCensusForMcSurveys(true);
    }

    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL_TEXT.DELETE_QUOTA.replace(
          '<QUALIFICATION_NAME>',
          this.qualification.qualification_name
        ),
        confirmButtonLabel: 'Yes',
      },
    });

    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this._actionAfterDeleteQuotas(false);
      }
    });
  }

  getbuttonText() {
    let buttonText = '';
    if (
      !this._valueExist(this.qualification.data[0].minValue) &&
      !this._valueExist(this.qualification.data[0].maxValue)
    ) {
      buttonText = 'All';
    }

    if (this._valueExist(this.qualification.data[0].minValue)) {
      buttonText += `${this.qualification.data[0].preFix || ''}${
        this.qualification.data[0].minValue
      }`;
    }

    if (this._valueExist(this.qualification.data[0].maxValue)) {
      buttonText += ` - ${this.qualification.data[0].preFix || ''}${
        this.qualification.data[0].maxValue
      }`;
    }

    return buttonText;
  }

  private _valueExist(value: number | string) {
    if (!value && value !== 0) {
      return false;
    }
    return true;
  }

  private _actionAfterDeleteQuotas(isMc: boolean = false) {
    this.quotas = [];
    this._resetQuotaFlags();

    this.quotaChange.emit({
      msg: 'deleteQuota',
      qualification: this.qualification,
      quotas: this.quotas,
      flexible: this.flexible,
      isMc,
    });
  }

  private _resetQuotaFlags() {
    this.disableCensusBtn = false;
    this.quotaFlags = {
      add: true,
      edit: false,
      census: this._quotaModalService.setCensusFlag(this.qualification),
      delete: false,
    };
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
